import { createContext, PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import {
    ChatTopic,
    ConversationModalPosition,
    ConversationModalSize
} from '../components/Chat/components/types/type';

export interface ChatContextType {
    ChatTopic?: ChatTopic;
    SelectedTopics?: ChatTopic[];
    Open: boolean;
    setSelectedTopics: React.Dispatch<React.SetStateAction<ChatContextType['SelectedTopics']>>;
    setChatTopic: React.Dispatch<React.SetStateAction<ChatContextType['ChatTopic']>>;
    setOpen: React.Dispatch<React.SetStateAction<ChatContextType['Open']>>;
    startChat: (selectedTopic: ChatTopic[]) => void;
    toggleChat: () => void;
    fromTo: { from: number; to: number };
    setFromTo: React.Dispatch<React.SetStateAction<ChatContextType['fromTo']>>;
    modalSize: ConversationModalSize;
    setModalSize: React.Dispatch<React.SetStateAction<ChatContextType['modalSize']>>;
    modalWidth: ConversationModalSize;
    setModalWidth: React.Dispatch<React.SetStateAction<ChatContextType['modalWidth']>>;
    searchVisible: boolean;
    setSearcVisible: React.Dispatch<React.SetStateAction<ChatContextType['searchVisible']>>;
    modalPosition: ConversationModalPosition;
    setModalPosition: React.Dispatch<React.SetStateAction<ChatContextType['modalPosition']>>;
}

export interface ChatContextProviderProps {
    children: ReactNode;
}

export const ChatContext = createContext<ChatContextType | null>(null);

export const ChatContextProvider = ({ children }: PropsWithChildren<{}>) => {
    const [ChatTopic, setChatTopic] = useState<ChatContextType['ChatTopic']>();
    const [SelectedTopics, setSelectedTopics] = useState<ChatContextType['SelectedTopics']>();
    const [Open, setOpen] = useState<ChatContextType['Open']>(false);
    const [fromTo, setFromTo] = useState<{ from: number; to: number }>({ from: 0, to: 9 });
    const [modalSize, setModalSize] = useState<ConversationModalSize>(ConversationModalSize.MID);
    const [modalWidth, setModalWidth] = useState<ConversationModalSize>(ConversationModalSize.MID);
    const [searchVisible, setSearcVisible] = useState<boolean>(false);
    const [modalPosition, setModalPosition] = useState<ConversationModalPosition>(
        ConversationModalPosition.RIGHT
    );

    const toggleChat = () => {
        setOpen(!Open);
    };

    const startChat = (selectedTopic: ChatTopic[]) => {
        // Order By LEVEL and set to selectedTopics
        const topics = selectedTopic.sort((a: ChatTopic, b: ChatTopic) => a.Level - b.Level);
        setSelectedTopics(topics);
        setChatTopic(topics[topics?.length - 1]);

        // open chat
        setOpen(true);
    };

    const value = useMemo(
        () => ({
            ChatTopic,
            setChatTopic,
            SelectedTopics,
            setSelectedTopics,
            Open,
            setOpen,
            toggleChat,
            fromTo,
            setFromTo,
            modalSize,
            setModalSize,
            modalWidth,
            setModalWidth,
            searchVisible,
            setSearcVisible,
            modalPosition,
            setModalPosition
        }),
        [ChatTopic, SelectedTopics, Open, modalSize, modalWidth, searchVisible, modalPosition]
    );

    return <ChatContext.Provider value={{ ...value, startChat }}>{children}</ChatContext.Provider>;
};
